import { Component, inject, signal, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TabsModule } from 'primeng/tabs';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { AvatarModule } from 'primeng/avatar';
import { ChipModule } from 'primeng/chip';
import { ClientService, Client } from '../../services/client';
import { FileService } from '../../services/file';
import { ToastService } from '../../services/toast';
import { ClientBranchesTab } from './tabs/client-branches';
import { ClientApiKeysTab } from './tabs/client-api-keys';
import { ClientCallbacksTab } from './tabs/client-callbacks';
import { ClientPaymentConfigTab } from './tabs/client-payment-config';
// The Messaging tab edits MessageRule directly. The old flat-toggle grid wrote BIT columns
// that nothing reads any more, so it would have looked like it worked and changed nothing.
import { MessagingRulesTab } from '../messaging/messaging-rules';
import { ClientPortalUsers } from './tabs/client-portal-users';

type TabKey = 'branches' | 'apikeys' | 'callbacks' | 'payment' | 'messaging' | 'portal';
const TAB_KEYS: TabKey[] = ['branches', 'apikeys', 'callbacks', 'payment', 'messaging', 'portal'];

@Component({
  selector: 'app-client-detail',
  standalone: true,
  imports: [
    CommonModule,
    TabsModule,
    ButtonModule,
    TagModule,
    AvatarModule,
    ChipModule,
    ClientBranchesTab,
    ClientApiKeysTab,
    ClientCallbacksTab,
    ClientPaymentConfigTab,
    MessagingRulesTab,
    ClientPortalUsers,
  ],
  templateUrl: './client-detail.html',
  styleUrls: ['./clients-shared.css', './client-detail.css'],
})
export class ClientDetail implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private svc = inject(ClientService);
  private fileSvc = inject(FileService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);

  clientId = signal<number | null>(null);
  client = signal<Client | null>(null);
  loading = signal(false);
  activeTab = signal<TabKey>('branches');
  /** Presigned URL for the client's logo (resolved from a guid, or a direct http URL). */
  logoSrc = signal<string>('');

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    // ?tab= deep-link survives refresh; default to branches.
    const tab = this.route.snapshot.queryParamMap.get('tab') as TabKey | null;
    if (tab && TAB_KEYS.includes(tab)) this.activeTab.set(tab);

    this.route.paramMap.subscribe((pm) => {
      const id = Number(pm.get('id'));
      if (!id) return;
      this.clientId.set(id);
      this.loadClient(id);
    });
  }

  private loadClient(id: number) {
    this.loading.set(true);
    this.svc.cltGetById(id).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not load client');
          return;
        }
        const c: Client | null = res?.data ?? null;
        this.client.set(c);
        this.resolveLogo(c?.logoUrl);
      },
      error: () => {
        this.loading.set(false);
        this.toast.error('Could not load client');
      },
    });
  }

  onTabChange(value: any) {
    const tab = value as TabKey;
    this.activeTab.set(tab);
    // Reflect the active tab in the URL so a refresh restores it.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** A logo value may be a direct http URL (legacy) or an S3 guid — resolve to a renderable URL. */
  private resolveLogo(v?: string | null) {
    this.logoSrc.set('');
    const val = (v ?? '').trim();
    if (!val) return;
    if (/^https?:\/\//i.test(val)) {
      this.logoSrc.set(val);
      return;
    }
    this.fileSvc.url(val).subscribe({
      next: (url) => this.logoSrc.set(url || ''),
      error: () => this.logoSrc.set(''),
    });
  }

  initials(name?: string | null): string {
    const n = (name || '').trim();
    if (!n) return '?';
    const parts = n.split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
  }

  back() {
    this.router.navigate(['/clients']);
  }

  edit() {
    const id = this.clientId();
    if (id) this.router.navigate(['/clients'], { queryParams: { edit: id } });
  }
}
